import React, { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import styled from 'styled-components';

import { DocumentIcon } from '../../../UI/DocumentIcon/DocumentIcon';
import { CloseIcon } from '../../../UI/NgIcons';
import { useGetAssetThumbnailUrlQuery } from '../../../../services';

export interface DocumentsPreviewTemplateItem {
  id: string;
  name: string;
  outputType: string;
  assetId?: number;
}

interface DocumentsPreviewDialogProps {
  open: boolean;
  title: string;
  templates: DocumentsPreviewTemplateItem[];
  onClose: () => void;
}

const StyledPanel = styled.div<{ $open: boolean }>`
  position: fixed;
  top: var(--top-nav-height, 60px);
  right: 0;
  bottom: 0;
  z-index: 99999999;
  width: ${({ $open }) => ($open ? '62%' : '0')};
  overflow: hidden;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.PresentationPreviewDialog.background};
  border-radius: 15px 0 0 0;
`;

const StyledInner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.PresentationPreviewDialog.buttonBackground};
  flex-shrink: 0;
`;

const StyledTitle = styled.span`
  flex: 2;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.fontFamily.main};
`;

const StyledHeaderRight = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledCloseButton = styled.button`
  background: ${({ theme }) => theme.PresentationPreviewDialog.buttonBackground};
  border: 1px solid ${({ theme }) => theme.PresentationPreviewDialog.buttonBorder};
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.textDimmed};
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.PresentationPreviewDialog.buttonBorder};
    color: ${({ theme }) => theme.palette.white};
  }

  svg {
    width: 13px;
    height: 13px;
  }
`;

const StyledStage = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 40px 16px;
  position: relative;
  min-height: 0;
`;


const StyledPreviewCard = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.palette.white};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  box-shadow: ${({ theme }) => theme.PresentationPreviewDialog.cardShadow};
`;

const StyledPreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;


const StyledPlaceholderLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  font-family: ${({ theme }) => theme.fontFamily.main};
`;

const StyledFilmstrip = styled.div`
  flex-shrink: 0;
  padding: 0 40px 14px;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  align-items: center;

  &::-webkit-scrollbar {
    height: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.PresentationPreviewDialog.scrollbarThumb};
    border-radius: 99px;
  }
`;

const StyledThumbnail = styled.button<{ $active: boolean; $thumbnailUrl?: string | null }>`
  flex-shrink: 0;
  width: 72px;
  aspect-ratio: 16 / 9;
  border-radius: ${({ theme }) => theme.radius.xSmall};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px;
  transition: all 0.15s;
  position: relative;
  overflow: hidden;
  font-family: ${({ theme }) => theme.fontFamily.main};
  background: ${({ $thumbnailUrl, theme }) =>
    $thumbnailUrl ? `url(${$thumbnailUrl}) center/cover no-repeat` : theme.palette.white};
  border: ${({ $active, theme }) =>
    $active ? `2px solid ${theme.palette.primaryButton}` : `1.5px solid ${theme.palette.grey}`};
  box-shadow: ${({ $active, theme }) => ($active ? theme.PresentationPreviewDialog.activeThumbnailRing : 'none')};

  &:hover {
    border-color: ${({ theme }) => theme.palette.grayLighter};
  }
`;

const StyledThumbLabel = styled.div`
  font-size: 8px;
  color: ${({ theme }) => theme.palette.textDimmed};
  text-align: center;
  padding: 0 2px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const TemplateThumbnail: FC<{ template: DocumentsPreviewTemplateItem; active: boolean; onClick: () => void }> = ({
  template,
  active,
  onClick,
}) => {
  const { data: thumbnailUrl } = useGetAssetThumbnailUrlQuery(
    template.assetId ? { assetId: template.assetId } : skipToken,
  );

  return (
    <StyledThumbnail key={template.id} $active={active} $thumbnailUrl={thumbnailUrl} onClick={onClick}>
      {!thumbnailUrl && <DocumentIcon documentType={template.outputType} />}
      <StyledThumbLabel>{template.name}</StyledThumbLabel>
    </StyledThumbnail>
  );
};

const TemplatePreviewImage: FC<{ template: DocumentsPreviewTemplateItem }> = ({ template }) => {
  const { data: thumbnailUrl } = useGetAssetThumbnailUrlQuery(
    template.assetId ? { assetId: template.assetId } : skipToken,
  );

  if (!thumbnailUrl) {
    return null;
  }

  return <StyledPreviewImage src={thumbnailUrl} alt={template.name} />;
};

export const DocumentsPreviewDialog: FC<DocumentsPreviewDialogProps> = ({
  open,
  title,
  templates,
  onClose,
}) => {
  const [activeTemplateIndex, setActiveTemplateIndex] = useState(0);

  const templateCount = templates.length;
  const safeTemplateIndex = Math.min(activeTemplateIndex, Math.max(0, templateCount - 1));
  const activeTemplate = templates[safeTemplateIndex];

  useEffect(() => {
    if (!open) return;
    setActiveTemplateIndex(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  return createPortal(
    <StyledPanel $open={open}>
      <StyledInner>
        <StyledHeader>
          <StyledTitle>
            {activeTemplate?.name || title}
          </StyledTitle>

          <StyledHeaderRight>
            <StyledCloseButton onClick={onClose}>
              <CloseIcon />
            </StyledCloseButton>
          </StyledHeaderRight>
        </StyledHeader>

        <StyledStage>
          <StyledPreviewCard>
            {activeTemplate ? (
              <TemplatePreviewImage key={activeTemplate.id} template={activeTemplate} />
            ) : (
              <StyledPlaceholderLabel>No templates selected</StyledPlaceholderLabel>
            )}
          </StyledPreviewCard>
        </StyledStage>

        {templateCount > 0 && (
          <StyledFilmstrip>
            {templates.map((template, index) => (
              <TemplateThumbnail
                key={template.id}
                template={template}
                active={index === safeTemplateIndex}
                onClick={() => setActiveTemplateIndex(index)}
              />
            ))}
          </StyledFilmstrip>
        )}
      </StyledInner>
    </StyledPanel>,
    document.body,
  );
};
